import { useState, useMemo, useEffect } from 'react';
import { BarChart } from '@mui/x-charts';
import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Text } from '@fragments';
import { getIntlLocale } from '@i18n';

import '../styles.scss';
import './styles.scss';

// CSS variable palette used to color stacked series. The actual color values
// are defined in `_charts/styles.scss` and resolved at runtime here because
// MUI x-charts passes `color` through d3-color (which can't parse `var(...)`).
const PALETTE_VARS = [
    '--chart-1', '--chart-2', '--chart-3', '--chart-4',
    '--chart-5', '--chart-6', '--chart-7', '--chart-8',
    '--chart-9', '--chart-10', '--chart-11', '--chart-12',
];

function moneySymbol(currency = 'BRL') {
    return String(currency).toUpperCase() === 'USD' ? 'US$' : 'R$';
}

function formatMoneyCompact(value, currency = 'BRL') {
    const symbol = moneySymbol(currency);
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${symbol}${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return `${symbol}${value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatMoneyFull(value, currency = 'BRL') {
    return `${moneySymbol(currency)} ${value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumberCompact(value) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(value);
}

function useResolvedPalette() {
    const [palette, setPalette] = useState(() => {
        if (typeof window === 'undefined') return [];
        return PALETTE_VARS.map(v =>
            getComputedStyle(document.documentElement).getPropertyValue(v).trim()
        );
    });
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const resolved = PALETTE_VARS.map(v =>
            getComputedStyle(document.documentElement).getPropertyValue(v).trim()
        );
        setPalette(resolved);
    }, []);
    return palette;
}

/**
 * StackedBarChart — composed bar chart with one bar per x-axis label,
 * stacking each series on top of the previous.
 *
 * @param {string}    title          chart title
 * @param {ReactNode} icon           leading icon
 * @param {Array}     series         [{ id, label, data: number[] }]
 * @param {Array}     labels         x-axis labels (length matches each series.data)
 * @param {string}    valueType      'number' | 'money'  (money values are pre-divided by 100)
 * @param {string}    unitSingular   tooltip noun for number type (singular)
 * @param {string}    unitPlural     tooltip noun for number type (plural)
 * @param {Object}    report         passed for the reload-dim state
 * @param {boolean}   reportLoading  passed for the reload-dim state
 */
export default function StackedBarChart({
    title,
    icon,
    series = [],
    labels = [],
    valueType = 'number',
    currency = 'BRL',
    unitSingular,
    unitPlural,
    report,
    reportLoading,
    // 'small' 16 (default) | 'medium' 20 | 'large' 24 | 'full' pill — the icon
    // chip follows (see .ts-chart-card in TimeSeriesChart/styles.scss).
    cornerRadius = 'small',
}) {
    const { t } = useTranslation('dashboard');
    const palette = useResolvedPalette();
    const isMoney = valueType === 'money';
    const radiusClass = cornerRadius !== 'small' ? ` ts-chart-card--radius-${cornerRadius}` : '';

    const formatShort = isMoney ? (value) => formatMoneyCompact(value, currency) : formatNumberCompact;

    function formatFull(value) {
        if (isMoney) return formatMoneyFull(value, currency);
        const n = Number(value) || 0;
        const noun = n === 1
            ? (unitSingular || t('charts.unit.item'))
            : (unitPlural || t('charts.unit.items'));
        return `${n.toLocaleString(getIntlLocale())} ${noun}`;
    }

    const muiSeries = useMemo(() => series.map((s, i) => ({
        id: `series-${i}`,
        data: s.data,
        label: typeof s.label === 'string' && s.label.length ? s.label : t('charts.seriesFallback', { index: i + 1 }),
        color: palette[i % (palette.length || 1)] || undefined,
        stack: 'total',
        valueFormatter: (v) => formatFull(v ?? 0),
    })), [series, palette, isMoney, unitSingular, unitPlural, t]);

    const total = useMemo(
        () => series.reduce(
            (acc, s) => acc + s.data.reduce((a, b) => a + (Number(b) || 0), 0),
            0,
        ),
        [series],
    );

    const hasData = series.length > 0 && series.some(s => s.data.some(v => Number(v) > 0));

    return (
        <div className={`ts-chart-card${radiusClass}${reportLoading && report ? ' ts-chart-card--reloading' : ''}`}>
            <div className="ts-chart-card__header">
                <div className="ts-chart-card__title">
                    {icon && <span className="ts-chart-card__icon">{icon}</span>}
                    <Text.h4 contrast={7}>{title}</Text.h4>
                    <Text.span contrast={4} className="ts-chart-card__count">
                        · {t('charts.total', { value: formatFull(total) })}
                    </Text.span>
                </div>
            </div>

            <div className="ts-chart-card__body ts-chart-card__body--tall">
                {reportLoading && !report ? (
                    <Skeleton variant="rounded" height={320} className="ts-chart-card__skeleton" />
                ) : !hasData ? (
                    <div className="ts-chart-card__empty">
                        <Text.p contrast={5}>{t('charts.empty')}</Text.p>
                    </div>
                ) : (
                    <>
                        <BarChart
                            height={280}
                            skipAnimation={false}
                            series={muiSeries}
                            xAxis={[{ data: labels, scaleType: 'band' }]}
                            yAxis={[{ valueFormatter: formatShort }]}
                            grid={{ horizontal: true }}
                            margin={{ top: 16, bottom: 28, left: isMoney ? 56 : 36, right: 16 }}
                            borderRadius={4}
                            sx={{ '.MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <div className="ts-chart-card__legend">
                            {muiSeries.map(s => (
                                <span key={s.id} className="ts-chart-card__legend-item">
                                    <span
                                        className="ts-chart-card__legend-mark"
                                        style={{ backgroundColor: s.color }}
                                    />
                                    <Text.span contrast={6}>{s.label}</Text.span>
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
